<template>
  <div>
    <!-- 页面标题 -->
    <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 20px;">
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>财务流水</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 搜索区域 -->
    <el-card style="margin-bottom: 20px;">
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="业务类型">
          <el-select v-model="searchForm.bizType" placeholder="请选择" clearable>
            <el-option label="收入" value="INCOME"></el-option>
            <el-option label="退款" value="REFUND"></el-option>
            <el-option label="支出" value="EXPENSE"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="支付渠道">
          <el-select v-model="searchForm.channel" placeholder="请选择" clearable>
            <el-option label="支付宝" value="ALIPAY"></el-option>
            <el-option label="模拟微信支付" value="MOCK_WECHAT"></el-option>
            <el-option label="模拟支付宝支付" value="MOCK_ALIPAY"></el-option>
            <el-option label="模拟支付(旧)" value="MOCK"></el-option>
            <el-option label="手工录入" value="MANUAL"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadLedgers" icon="el-icon-search">查询</el-button>
          <el-button @click="resetSearch" icon="el-icon-refresh">重置</el-button>
          <el-button type="success" @click="exportData" icon="el-icon-download">导出</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 流水列表 -->
    <el-card>
      <div slot="header">
        <span>财务流水列表</span>
        <div style="float: right;">
          <el-tag v-if="searchForm.bizType" :type="getTagType(searchForm.bizType)">
            {{ getBizTypeName(searchForm.bizType) }}
          </el-tag>
          <el-tag v-if="searchForm.channel" type="info" style="margin-left: 10px;">
            {{ getChannelName(searchForm.channel) }}
          </el-tag>
        </div>
      </div>

      <el-table 
        :data="ledgers" 
        v-loading="loading"
        stripe
        border>
        <el-table-column prop="id" label="流水号" width="80"></el-table-column>
        <el-table-column prop="bizType" label="类型" width="80">
          <template slot-scope="scope">
            <el-tag :type="getTagType(scope.row.bizType)" size="small">
              {{ getBizTypeName(scope.row.bizType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="subject" label="交易主题" show-overflow-tooltip min-width="200"></el-table-column>
        <el-table-column prop="channel" label="渠道" width="120">
          <template slot-scope="scope">
            <el-tag size="small" :type="getChannelTagType(scope.row.channel)">
              {{ getChannelName(scope.row.channel) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="金额" width="120" align="right">
          <template slot-scope="scope">
            <span :class="scope.row.amount >= 0 ? 'amount-positive' : 'amount-negative'">
              ¥{{ formatMoney(scope.row.amount) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="tradeNo" label="交易号" width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="occurredAt" label="发生时间" width="180">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.occurredAt) }}
          </template>
        </el-table-column>
        <el-table-column label="关联信息" width="150">
          <template slot-scope="scope">
            <div v-if="scope.row.orderId">
              <el-tag size="mini">订单: {{ scope.row.orderId }}</el-tag>
            </div>
            <div v-if="scope.row.sysMovie">
              <el-tag size="mini" type="warning">{{ scope.row.sysMovie.movieName }}</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="240" fixed="right">
          <template slot-scope="scope">
            <el-tooltip effect="dark" content="查看流水详情" placement="top" :enterable="false" :open-delay="500">
              <el-button type="success" @click="viewDetail(scope.row)" size="small">
                详情
              </el-button>
            </el-tooltip>
            <!-- 只有收入记录且未退款的才显示退款按钮 -->
            <el-tooltip v-if="scope.row.bizType === 'INCOME' && !hasRefunded(scope.row)" 
                       effect="dark" content="申请退款" placement="top" :enterable="false" :open-delay="500">
              <el-button type="warning" @click="showRefundDialog(scope.row)" size="small" style="margin-left: 8px;">
                退款
              </el-button>
            </el-tooltip>
            <!-- 已退款标识 -->
            <el-tag v-if="scope.row.bizType === 'INCOME' && hasRefunded(scope.row)" 
                   type="info" size="mini" style="margin-left: 8px;">已退款</el-tag>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pageInfo.pageNum"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageInfo.pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="pageInfo.total"
        style="margin-top: 20px; text-align: left;">
      </el-pagination>
    </el-card>

    <!-- 详情对话框 -->
    <el-dialog
      title="流水详情"
      :visible.sync="detailVisible"
      width="60%">
      <el-descriptions :column="2" border v-if="currentLedger">
        <el-descriptions-item label="流水号">{{ currentLedger.id }}</el-descriptions-item>
        <el-descriptions-item label="业务类型">
          <el-tag :type="getTagType(currentLedger.bizType)">
            {{ getBizTypeName(currentLedger.bizType) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="交易主题" :span="2">{{ currentLedger.subject }}</el-descriptions-item>
        <el-descriptions-item label="支付渠道">{{ getChannelName(currentLedger.channel) }}</el-descriptions-item>
        <el-descriptions-item label="金额">
          <span :class="currentLedger.amount >= 0 ? 'amount-positive' : 'amount-negative'">
            ¥{{ formatMoney(currentLedger.amount) }}
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="手续费">¥{{ formatMoney(currentLedger.fee) }}</el-descriptions-item>
        <el-descriptions-item label="实收金额">
          <span :class="getActualAmount(currentLedger) >= 0 ? 'amount-positive' : 'amount-negative'">
            ¥{{ formatMoney(getActualAmount(currentLedger)) }}
          </span>
        </el-descriptions-item>
        <el-descriptions-item label="第三方交易号">{{ currentLedger.tradeNo || '无' }}</el-descriptions-item>
        <el-descriptions-item label="发生时间">{{ formatDateTime(currentLedger.occurredAt) }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatDateTime(currentLedger.createTime) }}</el-descriptions-item>
        <el-descriptions-item label="关联订单">{{ currentLedger.orderId || '无' }}</el-descriptions-item>
        <el-descriptions-item label="支付记录ID">{{ currentLedger.paymentId || '无' }}</el-descriptions-item>
        <el-descriptions-item label="关联电影" v-if="currentLedger.sysMovie">
          {{ currentLedger.sysMovie.movieName }}
        </el-descriptions-item>
        <el-descriptions-item label="创建人" v-if="currentLedger.createdUser">
          {{ currentLedger.createdUser.userName }}
        </el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 退款确认对话框 -->
    <el-dialog
      title="退款确认"
      :visible.sync="refundDialogVisible"
      width="500px"
      :close-on-click-modal="false">
      <div v-if="refundLedger">
        <el-alert
          title="退款确认"
          type="warning"
          description="请确认是否要对此笔收入记录进行退款，退款后将生成一笔负值的退款记录。"
          show-icon
          :closable="false"
          style="margin-bottom: 20px;">
        </el-alert>
        
        <el-descriptions :column="1" border>
          <el-descriptions-item label="流水号">{{ refundLedger.id }}</el-descriptions-item>
          <el-descriptions-item label="交易主题">{{ refundLedger.subject }}</el-descriptions-item>
          <el-descriptions-item label="原始金额">¥{{ formatMoney(refundLedger.amount) }}</el-descriptions-item>
          <el-descriptions-item label="手续费">¥{{ formatMoney(refundLedger.fee) }}</el-descriptions-item>
          <el-descriptions-item label="实收金额">¥{{ formatMoney(getActualAmount(refundLedger)) }}</el-descriptions-item>
          <el-descriptions-item label="退款金额">¥{{ formatMoney(refundLedger.amount) }}</el-descriptions-item>
        </el-descriptions>

        <div style="margin-top: 20px;">
          <el-form :model="refundForm" :rules="refundRules" ref="refundForm" label-width="100px">
            <el-form-item label="退款原因" prop="reason">
              <el-input
                v-model="refundForm.reason"
                type="textarea"
                :rows="3"
                placeholder="请输入退款原因"
                maxlength="200"
                show-word-limit>
              </el-input>
            </el-form-item>
          </el-form>
        </div>
      </div>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="refundDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmRefund" :loading="refundLoading">
          确认退款
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "FinanceLedger",
  data() {
    return {
      searchForm: {
        bizType: '',
        channel: '',
        dateRange: []
      },
      ledgers: [],
      loading: false,
      pageInfo: {
        pageNum: 1,
        pageSize: 20,
        total: 0
      },
      detailVisible: false,
      currentLedger: null,
      // 退款相关数据
      refundDialogVisible: false,
      refundLedger: null,
      refundLoading: false,
      refundForm: {
        reason: ''
      },
      refundRules: {
        reason: [
          { required: true, message: '请输入退款原因', trigger: 'blur' },
          { min: 5, max: 200, message: '退款原因长度在 5 到 200 个字符', trigger: 'blur' }
        ]
      },
      // 存储已退款的流水ID，用于判断是否已退款
      refundedLedgerIds: new Set()
    }
  },
  created() {
    this.loadLedgers()
    this.loadRefundStatus()
  },
  methods: {
    async loadLedgers() {
      this.loading = true
      try {
        const params = {
          pageNum: this.pageInfo.pageNum,
          pageSize: this.pageInfo.pageSize,
          bizType: this.searchForm.bizType,
          channel: this.searchForm.channel
        }

        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startDate = this.searchForm.dateRange[0]
          params.endDate = this.searchForm.dateRange[1]
        }

        const { data: res } = await axios.get('api/admin/finance/ledger/search', { params })
        console.log('财务流水API响应:', res)
        
        if (res.code === 200) {
          // 检查数据格式
          if (res.data && Array.isArray(res.data.list)) {
            this.ledgers = res.data.list
            this.pageInfo.total = res.data.total || 0
          } else if (res.data && Array.isArray(res.data)) {
            // 如果直接返回数组
            this.ledgers = res.data
            this.pageInfo.total = res.data.length
          } else {
            console.warn('财务流水数据格式异常:', res.data)
            this.ledgers = []
            this.pageInfo.total = 0
          }
          console.log('设置财务流水数据:', this.ledgers.length, '条记录')
        } else {
          this.$message.error('获取财务流水失败: ' + res.message)
        }
      } catch (error) {
        console.error('加载财务流水失败:', error)
        this.$message.error('加载财务流水失败')
      } finally {
        this.loading = false
      }
    },

    resetSearch() {
      this.searchForm = {
        bizType: '',
        channel: '',
        dateRange: []
      }
      this.pageInfo.pageNum = 1
      this.loadLedgers()
    },

    async exportData() {
      try {
        const params = {
          bizType: this.searchForm.bizType,
          channel: this.searchForm.channel
        }

        if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
          params.startDate = this.searchForm.dateRange[0]
          params.endDate = this.searchForm.dateRange[1]
        }

        const response = await axios.get('api/admin/finance/ledger/export', { 
          params,
          responseType: 'blob'
        })

        // 创建下载链接
        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = `财务流水_${new Date().toISOString().split('T')[0]}.csv`
        link.click()

        this.$message.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        this.$message.error('导出失败')
      }
    },

    viewDetail(row) {
      this.currentLedger = row
      this.detailVisible = true
    },

    handleSizeChange(size) {
      this.pageInfo.pageSize = size
      this.pageInfo.pageNum = 1
      this.loadLedgers()
    },

    handleCurrentChange(page) {
      this.pageInfo.pageNum = page
      this.loadLedgers()
    },

    getBizTypeName(bizType) {
      const typeMap = {
        'INCOME': '收入',
        'REFUND': '退款',
        'EXPENSE': '支出'
      }
      return typeMap[bizType] || bizType
    },

    getChannelName(channel) {
      const channelMap = {
        'ALIPAY': '支付宝',
        'MOCK_WECHAT': '模拟微信支付',
        'MOCK_ALIPAY': '模拟支付宝支付',
        'MOCK': '模拟支付(旧)',
        'MANUAL': '手工录入'
      }
      return channelMap[channel] || channel
    },

    getChannelTagType(channel) {
      const typeMap = {
        'ALIPAY': 'success',
        'MOCK_WECHAT': 'primary',
        'MOCK_ALIPAY': 'warning',
        'MOCK': 'info',
        'MANUAL': 'info'
      }
      return typeMap[channel] || 'info'
    },

    getActualAmount(ledger) {
      if (!ledger) return 0
      // 如果数据库中有实收金额，直接使用，否则计算
      if (ledger.actualAmount !== null && ledger.actualAmount !== undefined) {
        return parseFloat(ledger.actualAmount) || 0
      }
      // 兼容旧数据，使用计算方式
      const amount = parseFloat(ledger.amount) || 0
      const fee = parseFloat(ledger.fee) || 0
      return amount - fee
    },

    getTagType(bizType) {
      const typeMap = {
        'INCOME': 'success',
        'REFUND': 'warning',
        'EXPENSE': 'danger'
      }
      return typeMap[bizType] || 'info'
    },

    formatMoney(amount) {
      if (!amount && amount !== 0) return '0.00'
      return Number(amount).toFixed(2)
    },

    formatDateTime(dateTime) {
      if (!dateTime) return ''
      return new Date(dateTime).toLocaleString('zh-CN')
    },

    // 退款相关方法
    hasRefunded(ledger) {
      // 检查是否已经退款
      return this.refundedLedgerIds.has(ledger.id)
    },

    showRefundDialog(ledger) {
      this.refundLedger = ledger
      this.refundForm.reason = ''
      this.refundDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.refundForm) {
          this.$refs.refundForm.clearValidate()
        }
      })
    },

    async confirmRefund() {
      // 表单验证
      const valid = await this.$refs.refundForm.validate().catch(() => false)
      if (!valid) return

      // 二次确认
      const confirmed = await this.$confirm(
        `确定要对流水号 ${this.refundLedger.id} 进行退款吗？退款后将无法撤销。`,
        '退款确认',
        {
          confirmButtonText: '确定退款',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).catch(() => false)

      if (!confirmed) return

      this.refundLoading = true
      
      try {
        const refundData = {
          ledgerId: this.refundLedger.id,
          reason: this.refundForm.reason
        }

        const { data: res } = await axios.post('api/admin/finance/ledger/refund', refundData)
        
        if (res.code === 200) {
          this.$message.success('退款成功')
          
          // 标记为已退款
          this.refundedLedgerIds.add(this.refundLedger.id)
          
          // 关闭对话框
          this.refundDialogVisible = false
          
          // 刷新列表
          this.loadLedgers()
        } else {
          this.$message.error('退款失败: ' + res.message)
        }
      } catch (error) {
        console.error('退款失败:', error)
        if (error.response && error.response.data && error.response.data.message) {
          this.$message.error('退款失败: ' + error.response.data.message)
        } else {
          this.$message.error('退款失败，请稍后重试')
        }
      } finally {
        this.refundLoading = false
      }
    },

    async loadRefundStatus() {
      // 加载已退款的流水ID列表
      try {
        const { data: res } = await axios.get('api/admin/finance/ledger/refunded-ids')
        if (res.code === 200 && Array.isArray(res.data)) {
          this.refundedLedgerIds = new Set(res.data)
        }
      } catch (error) {
        console.error('加载退款状态失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.search-form {
  margin-bottom: 0;
}

.amount-positive {
  color: #67C23A;
  font-weight: bold;
}

.amount-negative {
  color: #F56C6C;
  font-weight: bold;
}

.el-tag {
  margin: 2px;
}
</style>
